import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';

import { BadStatusError, cfetch } from '@confluence/network';

import type { AdfNodeLike } from './orderSuggestionsByDocument.ts';

const fetchContentAdf = async ({
	contentId,
	status,
	signal,
}: {
	contentId: string;
	status: 'draft' | 'current';
	signal?: AbortSignal;
}): Promise<AdfNodeLike | null> => {
	let response: Response;
	try {
		response = await cfetch(
			`/wiki/rest/api/content/${encodeURIComponent(contentId)}?status=${status}&expand=body.atlas_doc_format`,
			{ method: 'GET', credentials: 'include', signal, ignoreNoNetworkError: true },
		);
	} catch (error) {
		if (BadStatusError.isBadStatusError(error) && error.response.status === 404) {
			return null;
		}
		throw error;
	}

	const data = (await response.json()) as {
		body?: { atlas_doc_format?: { value?: string } };
	};
	const value = data?.body?.atlas_doc_format?.value;
	if (!value) {
		throw new Error(`Get ${status} content response has no ADF body`);
	}
	return JSON.parse(value) as AdfNodeLike;
};

export const fetchLatestDraftAdf = async ({
	contentId,
	signal,
}: {
	contentId: string;
	signal?: AbortSignal;
}): Promise<AdfNodeLike> => {
	const draft = await fetchContentAdf({ contentId, status: 'draft', signal });
	if (draft) {
		return draft;
	}
	const current = await fetchContentAdf({ contentId, status: 'current', signal });
	if (!current) {
		throw new Error('Get content request failed: 404');
	}
	return current;
};

export const fetchPublishedPageSuggestions = async ({
	contentAri,
	signal,
}: {
	contentAri: string;
	signal?: AbortSignal;
}): Promise<Suggestion[]> => {
	const url = `/gateway/api/assist/api/ai/v2/ai-feature/content-suggested-edits/suggestions?content_ari=${encodeURIComponent(
		contentAri,
	)}&status=PENDING`;

	const response = await cfetch(url, {
		method: 'GET',
		credentials: 'include',
		mode: 'cors',
		headers: {
			'Content-Type': 'application/json;charset=UTF-8',
			'X-Channel-Id': 'editor',
			'X-Product': 'confluence',
			'X-Experience-Id': 'editor',
		},
		signal,
		ignoreNoNetworkError: true,
	});

	const data = (await response.json()) as {
		ai_feature_output?: { items?: Suggestion[] };
	};
	const items = data?.ai_feature_output?.items;
	return Array.isArray(items) ? items : [];
};
